// Shift/round grouping for the Patrol and Attendance history tables.
//
// A schedule that runs overnight (patrol 22:00–06:00, a night attendance shift
// 20:00–05:00) spans two calendar dates. Listed newest-first, that reads as if
// the date jumps backwards halfway down the table: the 22:00/23:00 rows of one
// night sit above the 00:00–06:00 rows of the night before. Nothing was ever
// mis-sorted — the round boundaries were simply invisible.
//
// So cut the history on the schedule's own start/end rather than on midnight or
// on an inferred gap. The anchor mirrors the backends exactly
// (guard_patrol._route_ticks_around, attendance._in_window): a round begins at
// the schedule's `start` and runs to `end`, rolling into the next day whenever
// `end` is not after `start`. Every row therefore belongs to the most recent
// `start` at or before it.

const hhmm = (s, fallback) => {
  const [h, m] = String(s || fallback).split(':');
  return [Number(h) || 0, Number(m) || 0];
};

// { start, end } as local Date objects for the round containing epoch `ts`.
export function roundBounds(ts, sched, defStart = '00:00', defEnd = '00:00') {
  const [sh, sm] = hhmm(sched?.start, defStart);
  const [eh, em] = hhmm(sched?.end, defEnd);
  const start = new Date(ts * 1000);
  start.setHours(sh, sm, 0, 0);
  if (start.getTime() > ts * 1000) start.setDate(start.getDate() - 1);
  const end = new Date(start);
  end.setHours(eh, em, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);   // overnight round
  return { start, end };
}

// rows → [{ key, owner, sched, start, end, rows }], newest round first and
// newest row first inside it — i.e. the order these tables already used, with
// the seams between rounds now explicit.
//
//   timeOf   row → epoch seconds
//   ownerOf  row → { key, label, sched } for the route/checkpoint it belongs to
export function groupRounds(rows, timeOf, ownerOf, defStart, defEnd) {
  const by = new Map();
  rows.forEach(r => {
    const ts = timeOf(r);
    if (!Number.isFinite(ts)) return;
    const owner = ownerOf(r) || {};
    const { start, end } = roundBounds(ts, owner.sched, defStart, defEnd);
    const key = `${owner.key || ''}|${start.getTime()}`;
    if (!by.has(key)) by.set(key, { key, owner: owner.label || '', sched: owner.sched, start, end, rows: [] });
    by.get(key).rows.push(r);
  });
  const out = [...by.values()];
  out.forEach(g => g.rows.sort((a, b) => timeOf(b) - timeOf(a)));
  return out.sort((a, b) => b.start - a.start);
}

const DM  = { day: '2-digit', month: 'short' };
const DMY = { day: '2-digit', month: 'short', year: 'numeric' };

// "05 Sep 2026" for a same-day round, "05 → 06 Sep 2026" for an overnight one.
export function roundLabel(g) {
  return g.start.toDateString() === g.end.toDateString()
    ? g.start.toLocaleDateString([], DMY)
    : `${g.start.toLocaleDateString([], DM)} → ${g.end.toLocaleDateString([], DMY)}`;
}
